import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { PatientForm } from "../components/PatientForm";
import { ReviewForm } from "../components/ReviewForm";
import { AtelierButton } from "../components/AtelierButton";
import { Atelier1Button } from "../components/Atelier1Button";
import { Atelier2Button } from "../components/Atelier2Button";
import { Atelier3Button } from "../components/Atelier3Button";
import { Atelier4Button } from "../components/Atelier4Button";
import { Atelier5Button } from "../components/Atelier5Button";
import { ReferatMedicalButton } from "../components/ReferatMedicalButton";
import { AdeverintaButton, hasAdeverinta } from "../components/AdeverintaButton";
import { AdeverintaGeneralaButton } from "../components/AdeverintaGeneralaButton";
import { IstoricSportiv } from "../components/IstoricSportiv";
import { FisaCompletaDialog } from "../components/FisaCompletaDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AttachmentsSection } from "../components/AttachmentsSection";
import { RevenireCard } from "../components/RevenireCard";
import { PatientFlagsSection } from "../components/PatientFlagsSection";
import { patientApi, attachmentsApi, revenireApi } from "../services/api";
import { toast } from "sonner";
import { formatDate, formatDateTime, getInitials } from "../lib/utils";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Calendar,
  Pencil,
  Trash2,
  Plus,
  Dumbbell,
  Heart,
  Activity,
  ClipboardList,
  FileText,
  Clock,
  Eye,
  Award,
  History,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Paperclip,
  RotateCcw,
  Loader2,
} from "lucide-react";


// Redă un snapshot al formularului (6 secțiuni) în HTML citibil, pentru dialogul din Consultații.
// Ascunde câmpurile goale și normalizează valorile bool → „Da/Nu".
const renderFormSnapshotHTML = (snap) => {
  const esc = (v) => String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  const fmtVal = (v) => {
    if (v === true) return "Da";
    if (v === false) return "Nu";
    if (Array.isArray(v)) return v.length ? esc(v.join(", ")) : "";
    if (v === null || v === undefined) return "";
    if (typeof v === "object") {
      try { return `<pre style="white-space:pre-wrap;font-size:11px;background:#f8fafc;padding:6px;border-radius:4px">${esc(JSON.stringify(v, null, 2))}</pre>`; }
      catch { return ""; }
    }
    return esc(String(v));
  };
  const renderSection = (title, obj) => {
    if (!obj || typeof obj !== "object") return "";
    const rows = Object.entries(obj)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
      .map(([k, v]) => `<tr><td style="padding:4px 8px;font-weight:600;color:#334155;white-space:nowrap;vertical-align:top">${esc(k)}</td><td style="padding:4px 8px">${fmtVal(v)}</td></tr>`)
      .join("");
    if (!rows) return "";
    return `<div style="margin-bottom:14px"><h4 style="margin:0 0 6px;font-size:13px;text-transform:uppercase;letter-spacing:.3pt;border-bottom:1px solid #cbd5e1;padding-bottom:2pt">${esc(title)}</h4><table style="width:100%;border-collapse:collapse;font-size:12px">${rows}</table></div>`;
  };
  return [
    renderSection("Date personale", snap.date_personale),
    renderSection("Informații sportive", snap.info_sportive),
    renderSection("Istoric medical", snap.istoric_medical),
    renderSection("Informații COVID", snap.info_covid),
    renderSection("Efort fizic", snap.efort_fizic),
    renderSection("Informații seniori", snap.info_seniori),
  ].filter(Boolean).join("") || `<p style="color:#64748b;font-style:italic">Nu există date snapshot pentru această înscriere.</p>`;
};

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isAddingReview, setIsAddingReview] = useState(false);
  const [viewingFisa, setViewingFisa] = useState(null);
  // Snapshot HTML viewer (Referat / Concluzii aviz din istoric)
  const [viewingSnapshot, setViewingSnapshot] = useState(null);
  // Notițe libere admin (Fișiere → Fișiere atașate de admin) — se salvează la blur
  const [adminNotes, setAdminNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  // Acte medicale necesare de adus (Fișiere → sub Fișiere atașate de admin) — save on blur
  const [acteMedicale, setActeMedicale] = useState("");
  const [savingActe, setSavingActe] = useState(false);

  useEffect(() => {
    // Sincronizează starea locală cu datele pacientului la (re)load
    if (patient) setAdminNotes(patient.admin_notes || "");
  }, [patient?.id, patient?.admin_notes]);

  useEffect(() => {
    if (patient) setActeMedicale(patient.acte_medicale_necesare || "");
  }, [patient?.id, patient?.acte_medicale_necesare]);

  const handleAdminNotesBlur = async () => {
    if (!patient) return;
    if ((patient.admin_notes || "") === adminNotes) return;
    setSavingNotes(true);
    try {
      const updated = await patientApi.update(id, { admin_notes: adminNotes });
      setPatient(updated);
      toast.success("Notițe salvate");
    } catch (err) {
      console.error("Failed to save admin_notes:", err);
      toast.error("Eroare la salvarea notițelor");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleActeMedicaleBlur = async () => {
    if (!patient) return;
    if ((patient.acte_medicale_necesare || "") === acteMedicale) return;
    setSavingActe(true);
    try {
      const updated = await patientApi.update(id, { acte_medicale_necesare: acteMedicale });
      setPatient(updated);
      toast.success("Acte medicale salvate");
    } catch (err) {
      console.error("Failed to save acte_medicale_necesare:", err);
      toast.error("Eroare la salvarea actelor medicale");
    } finally {
      setSavingActe(false);
    }
  };

  useEffect(() => {
    loadPatient();
  }, [id]);

  const loadPatient = async () => {
    try {
      const data = await patientApi.getById(id);
      setPatient(data);
    } catch (error) {
      console.error("Failed to load patient:", error);
      toast.error("Eroare la încărcarea datelor");
      navigate("/admin/patients");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (data) => {
    setIsSaving(true);
    try {
      const updated = await patientApi.update(id, data);
      setPatient(updated);
      setIsEditing(false);
      toast.success("Datele au fost actualizate!");
    } catch (error) {
      console.error("Failed to update patient:", error);
      toast.error("Eroare la actualizare");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await patientApi.delete(id);
      toast.success("Sportivul a fost șters");
      navigate("/admin/patients");
    } catch (error) {
      console.error("Failed to delete patient:", error);
      toast.error("Eroare la ștergere");
    }
  };

  const handleAddReview = async (data) => {
    setIsAddingReview(true);
    try {
      const updated = await patientApi.addReview(id, data);
      setPatient(updated);
      setShowReviewForm(false);
      toast.success("Consultație adăugată cu succes!");
    } catch (error) {
      console.error("Failed to add review:", error);
      toast.error("Eroare la adăugarea consultației");
    } finally {
      setIsAddingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await patientApi.deleteReview(id, reviewId);
      setPatient((prev) => ({
        ...prev,
        reviews: prev.reviews.filter((r) => r.id !== reviewId),
      }));
      toast.success("Consultația a fost ștearsă");
    } catch (error) {
      console.error("Failed to delete review:", error);
      toast.error("Eroare la ștergere");
    }
  };

  const handleDeleteFisa = async (fisaId) => {
    try {
      const updated = await patientApi.deleteFisa(id, fisaId);
      setPatient(updated);
      toast.success("Fișa a fost ștearsă");
    } catch (error) {
      console.error("Failed to delete fisa:", error);
      toast.error("Eroare la ștergere fișă");
    }
  };

  // ------- Attachments (fișiere/poze atașate pacientului) -------
  const handleAdminUpload = async (file, onProgress) => {
    const att = await attachmentsApi.adminUpload(id, file, onProgress);
    // Sync state: adăugăm în lista pacientului
    setPatient((prev) => ({
      ...prev,
      attachments: [...(prev.attachments || []), att],
    }));
    return att;
  };

  const handleAttachmentDelete = async (fileId) => {
    await attachmentsApi.adminDelete(id, fileId);
    setPatient((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((a) => a.id !== fileId),
    }));
  };

  const handleAttachmentView = async (att) => {
    // Deschidem fișierul într-o fereastră nouă printr-un blob (păstrează cookies/credentials)
    const url = attachmentsApi.adminViewUrl(id, att.id);
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error("Nu s-a putut deschide fișierul.");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const w = window.open(objectUrl, "_blank");
    // Curățăm URL-ul după 30s (destule pentru încărcare)
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    if (!w) {
      toast.warning("Popup-ul a fost blocat. Verifică setările browser-ului.");
    }
  };

  // ------- Reveniri (note post-consultație cu text + attachments) -------
  const [addingRevenire, setAddingRevenire] = useState(false);
  const handleAddRevenire = async () => {
    if (addingRevenire) return;
    setAddingRevenire(true);
    try {
      const rev = await revenireApi.create(id, "");
      setPatient((prev) => ({
        ...prev,
        reveniri: [...(prev.reveniri || []), rev],
      }));
      toast.success("Revenire nouă adăugată");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la crearea revenirii");
    } finally {
      setAddingRevenire(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="patient-detail-loading" className="page-container">
        <Skeleton className="h-8 w-32 mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-96 rounded-xl lg:col-span-2" />
        </div>
      </div>
    );
  }

  if (!patient) return null;

  const dp = patient.date_personale || {};
  const is = patient.info_sportive || {};
  const im = patient.istoric_medical || {};
  const ef = patient.efort_fizic || {};
  const decl = patient.declaratii || {};
  const reviews = patient.reviews || [];

  const fullName = `${dp.nume || ''} ${dp.prenume || ''}`.trim() || 'Fără nume';
  const numarOrdine = patient.numar_ordine || 0;

  const InfoItem = ({ label, value, icon: Icon }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-2 text-sm">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
        <div>
          <span className="text-muted-foreground">{label}:</span>
          <span className="ml-1 font-medium">{value}</span>
        </div>
      </div>
    );
  };

  // Pentru întrebări medicale: "Nu" = bine (bifat verde), "Da" = problemă (X roșu)
  const MedicalYesNoItem = ({ label, value }) => {
    if (!value) return null;
    const isYes = value === "Da";
    return (
      <div className="flex items-center gap-2 text-sm">
        {isYes ? (
          <XCircle className="w-4 h-4 text-red-500" />
        ) : (
          <CheckCircle className="w-4 h-4 text-emerald-500" />
        )}
        <span>{label}</span>
        <Badge variant={isYes ? "destructive" : "secondary"} className="ml-auto">
          {value}
        </Badge>
      </div>
    );
  };

  // Pentru întrebări generale: "Da" = bifat, "Nu" = nebifat
  const YesNoItem = ({ label, value }) => {
    if (!value) return null;
    const isYes = value === "Da";
    return (
      <div className="flex items-center gap-2 text-sm">
        {isYes ? (
          <CheckCircle className="w-4 h-4 text-emerald-500" />
        ) : (
          <XCircle className="w-4 h-4 text-muted-foreground" />
        )}
        <span>{label}</span>
        <Badge variant={isYes ? "default" : "secondary"} className="ml-auto">
          {value}
        </Badge>
      </div>
    );
  };

  return (
    <div data-testid="patient-detail-page" className="page-container">
      {/* Back Link */}
      <Link to="/admin/patients">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 gap-2"
          data-testid="back-to-patients"
        >
          <ArrowLeft className="w-4 h-4" />
          Înapoi la Sportivi
        </Button>
      </Link>

      {isEditing ? (
        /* Edit Mode */
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              Editează Sportiv
            </h1>
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              data-testid="cancel-edit"
            >
              Anulează
            </Button>
          </div>
          <div className="max-w-4xl">
            <PatientForm
              initialData={patient}
              onSubmit={handleUpdate}
              isLoading={isSaving}
              submitLabel="Salvează Modificările"
            />
          </div>
        </div>
      ) : (
        /* View Mode */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Sidebar - Patient Info */}
          <div className="space-y-4 animate-slide-in">
            {/* Basic Info Card */}
            <Card>
              <CardContent className="p-6">
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-2xl font-semibold text-primary">
                    {getInitials(fullName)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="font-mono text-base">
                        #{numarOrdine}
                      </Badge>
                      {patient.numar_viza && (
                        <Badge
                          className="bg-emerald-600 text-white border-emerald-700 font-medium text-base"
                          data-testid="patient-numar-viza"
                          title={`Viză generată ${patient.numar_viza_date ? new Date(patient.numar_viza_date).toLocaleDateString("ro-RO") : ""}`}
                        >
                          Nr. ord. {patient.numar_viza}
                        </Badge>
                      )}
                    </div>
                    <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Work Sans', sans-serif" }} data-testid="patient-name">
                      {fullName}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary">{dp.sex || 'N/A'}</Badge>
                      {dp.data_nasterii && (
                        <span className="text-sm text-muted-foreground">
                          {formatDate(dp.data_nasterii)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-3 text-sm">
                  <InfoItem label="CNP" value={dp.cnp} icon={FileText} />
                  <InfoItem label="Telefon" value={dp.telefon} icon={Phone} />
                  <InfoItem label="Localitate" value={dp.localitate} icon={MapPin} />
                  <div className="flex items-center gap-3 pt-2 border-t">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      Înregistrat: {formatDate(patient.created_at)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="flex-1 gap-2"
                    data-testid="edit-patient-btn"
                  >
                    <Pencil className="w-4 h-4" />
                    Editează
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="gap-2"
                        data-testid="delete-patient-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Șterge Sportiv?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Această acțiune va șterge permanent fișa lui {fullName} și toate datele asociate.
                          Acțiunea nu poate fi anulată.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-delete">Anulează</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          data-testid="confirm-delete"
                        >
                          Șterge
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>

            {/* Sport Info Card */}
            {(is.sport_practicat || is.club_sportiv) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Dumbbell className="w-4 h-4 text-primary" />
                    Informații Sportive
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoItem label="Sport" value={is.sport_practicat} />
                  <InfoItem label="Post/Categorie" value={is.post_categorie} />
                  <InfoItem label="Club" value={is.club_sportiv} />
                  <InfoItem label="Antrenor" value={is.antrenor} />
                  <InfoItem label="Antrenamente/săpt" value={is.antrenamente_saptamana} />
                </CardContent>
              </Card>
            )}

            {/* Declarații Card */}
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  Declarații
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  {decl.declaratie_boli ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-amber-900">Declarație boli</span>
                </div>
                <div className="flex items-center gap-2">
                  {decl.acord_date_personale ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-amber-900">Acord date personale</span>
                </div>
                <div className="flex items-center gap-2">
                  {decl.cunostinta_anulare ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-amber-900">Cunoștință anulare</span>
                </div>
              </CardContent>
            </Card>

            {/* Ateliere Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-primary" />
                  Ateliere / Fișe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-3 p-3 rounded-md border bg-muted/30" data-testid="flags-wrapper">
                  <PatientFlagsSection
                    patient={patient}
                    onUpdated={(flags) => setPatient((prev) => (prev ? { ...prev, ...flags } : prev))}
                  />
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Atelier1Button
                    patientId={patient.id}
                    patientData={patient}
                    atelierData={patient.atelier_1}
                    onUpdate={setPatient}
                  />
                  <Atelier2Button
                    patientId={patient.id}
                    patientData={patient}
                    atelierData={patient.atelier_2}
                    onUpdate={setPatient}
                  />
                  <Atelier3Button
                    patient={patient}
                    onUpdate={setPatient}
                  />
                  <Atelier4Button
                    patientId={patient.id}
                    patientData={patient}
                    atelierData={patient.atelier_4}
                    onUpdate={setPatient}
                  />
                  <Atelier5Button
                    patientId={patient.id}
                    patientData={patient}
                    atelierData={patient.atelier_5}
                    onUpdate={setPatient}
                  />
                </div>
                <div className="mt-2">
                  <ReferatMedicalButton patient={patient} onUpdate={setPatient} />
                </div>
                {hasAdeverinta(is.sport_practicat) && (
                  <div className="mt-2">
                    <AdeverintaButton patient={patient} />
                  </div>
                )}
                <div className="mt-2">
                  <AdeverintaGeneralaButton patient={patient} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Content - Medical Info & Reviews */}
          <div className="lg:col-span-2 animate-fade-in">
            <Tabs defaultValue="medical" className="w-full">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <TabsList className="mb-0">
                <TabsTrigger value="reveniri" data-testid="tab-reveniri">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reveniri ({(patient.reveniri || []).length})
                </TabsTrigger>
                <TabsTrigger value="medical" data-testid="tab-medical">
                  <Heart className="w-4 h-4 mr-2" />
                  Istoric Medical
                </TabsTrigger>
                <TabsTrigger value="efort" data-testid="tab-efort">
                  <Activity className="w-4 h-4 mr-2" />
                  Efort Fizic
                </TabsTrigger>
                <TabsTrigger value="reviews" data-testid="tab-reviews">
                  <Clock className="w-4 h-4 mr-2" />
                  Consultații ({(patient.fise_complete || []).length + reviews.length})
                </TabsTrigger>
                <TabsTrigger value="istoric" data-testid="tab-istoric">
                  <History className="w-4 h-4 mr-2" />
                  Istoric sportiv ({(patient.istoric_ateliere || []).length})
                </TabsTrigger>
                <TabsTrigger value="fisiere" data-testid="tab-fisiere">
                  <Paperclip className="w-4 h-4 mr-2" />
                  Fișiere ({(patient.attachments || []).length})
                </TabsTrigger>
                </TabsList>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/admin/patients/${id}/fisa-tip-print`, "_blank")}
                  className="ml-auto gap-2"
                  data-testid="btn-genereaza-fisa"
                >
                  <FileText className="w-4 h-4" />
                  Generează fișă
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/admin/patients/${id}/provizorie-print`, "_blank")}
                  className="gap-2"
                  data-testid="btn-genereaza-provizorie"
                >
                  <FileText className="w-4 h-4" />
                  Generează provizorie
                </Button>
              </div>

              {/* Reveniri Tab — note post-consultație cu text + poze/fișiere */}
              <TabsContent value="reveniri" className="space-y-4">
                <Card>
                  <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Reveniri post-consultație</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Adaugă o rubrică nouă pentru fiecare vizită de control. Poți scrie observații și atașa poze sau documente.
                      </p>
                    </div>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleAddRevenire}
                      disabled={addingRevenire}
                      data-testid="add-revenire-btn"
                    >
                      {addingRevenire ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Adaugă revenire
                    </Button>
                  </CardHeader>
                </Card>

                {(patient.reveniri || []).length === 0 && (
                  <Card>
                    <CardContent className="py-10 text-center text-muted-foreground">
                      <RotateCcw className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Nu există reveniri înregistrate pentru acest sportiv.</p>
                      <p className="text-xs mt-1">Apasă {`„Adaugă revenire"`} pentru a crea prima notă.</p>
                    </CardContent>
                  </Card>
                )}

                {(patient.reveniri || []).map((rev, idx) => (
                  <RevenireCard
                    key={rev.id}
                    patientId={id}
                    revenire={rev}
                    index={idx}
                    attachments={(patient.attachments || []).filter((a) => a.revenire_id === rev.id)}
                    onTextUpdated={(rid, updated) =>
                      setPatient((prev) => ({
                        ...prev,
                        reveniri: (prev.reveniri || []).map((r) => (r.id === rid ? { ...r, ...updated } : r)),
                      }))
                    }
                    onDeleted={(rid) =>
                      setPatient((prev) => ({
                        ...prev,
                        reveniri: (prev.reveniri || []).filter((r) => r.id !== rid),
                        attachments: (prev.attachments || []).filter((a) => a.revenire_id !== rid),
                      }))
                    }
                    onAttachmentAdded={(att) =>
                      setPatient((prev) => ({
                        ...prev,
                        attachments: [...(prev.attachments || []), att],
                      }))
                    }
                    onAttachmentRemoved={(fid) =>
                      setPatient((prev) => ({
                        ...prev,
                        attachments: (prev.attachments || []).filter((a) => a.id !== fid),
                      }))
                    }
                  />
                ))}
              </TabsContent>

              {/* Istoric Medical Tab */}
              <TabsContent value="medical" className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Antecedente Familiale</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <MedicalYesNoItem
                      label="Are antecedente medicale familiale"
                      value={im.antecedente_familie}
                    />
                    {im.antecedente_familie === "Da" && im.antecedente_familie_detalii && (
                      <div className="mt-2 p-3 bg-muted rounded-md border-l-2 border-primary/40" data-testid="antecedente-familie-detalii">
                        <p className="text-xs text-muted-foreground mb-1">Specificare:</p>
                        <p className="text-sm whitespace-pre-wrap">{im.antecedente_familie_detalii}</p>
                      </div>
                    )}
                    {/* Retrocompatibilitate: text liber salvat anterior (înainte de switch la Da/Nu) */}
                    {im.antecedente_familie && im.antecedente_familie !== "Da" && im.antecedente_familie !== "Nu" && (
                      <div className="mt-2 p-3 bg-muted rounded-md" data-testid="antecedente-familie-legacy">
                        <p className="text-sm whitespace-pre-wrap">{im.antecedente_familie}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Antecedente Personale</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <MedicalYesNoItem
                      label="Are antecedente medicale personale"
                      value={im.antecedente_personale}
                    />
                    {im.antecedente_personale === "Da" && im.antecedente_personale_detalii && (
                      <div className="mt-2 p-3 bg-muted rounded-md border-l-2 border-primary/40" data-testid="antecedente-personale-detalii">
                        <p className="text-xs text-muted-foreground mb-1">Specificare:</p>
                        <p className="text-sm whitespace-pre-wrap">{im.antecedente_personale_detalii}</p>
                      </div>
                    )}
                    {im.antecedente_personale && im.antecedente_personale !== "Da" && im.antecedente_personale !== "Nu" && (
                      <div className="mt-2 p-3 bg-muted rounded-md" data-testid="antecedente-personale-legacy">
                        <p className="text-sm whitespace-pre-wrap">{im.antecedente_personale}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Stare de Sănătate</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <MedicalYesNoItem label="Modificări stare sănătate" value={im.modificari_sanatate} />
                    {im.modificari_sanatate === "Da" && im.modificari_sanatate_detalii && (
                      <div className="mt-1 p-3 bg-muted rounded-md border-l-2 border-primary/40">
                        <p className="text-xs text-muted-foreground mb-1">Specificare:</p>
                        <p className="text-sm whitespace-pre-wrap">{im.modificari_sanatate_detalii}</p>
                      </div>
                    )}
                    <MedicalYesNoItem label="Accidentări sportive (6 luni)" value={im.accidentari_sportive} />
                    {im.accidentari_sportive === "Da" && im.accidentari_sportive_detalii && (
                      <div className="mt-1 p-3 bg-muted rounded-md border-l-2 border-primary/40">
                        <p className="text-xs text-muted-foreground mb-1">Specificare:</p>
                        <p className="text-sm whitespace-pre-wrap">{im.accidentari_sportive_detalii}</p>
                      </div>
                    )}
                    <MedicalYesNoItem label="Internări medicale (6 luni)" value={im.internari_medicale} />
                    {im.internari_medicale === "Da" && im.internari_medicale_detalii && (
                      <div className="mt-1 p-3 bg-muted rounded-md border-l-2 border-primary/40">
                        <p className="text-xs text-muted-foreground mb-1">Specificare:</p>
                        <p className="text-sm whitespace-pre-wrap">{im.internari_medicale_detalii}</p>
                      </div>
                    )}
                    <MedicalYesNoItem label="Tratament medicamentos" value={im.tratament_medicamentos} />
                    {im.tratament_medicamentos === "Da" && im.tratament_detalii && (
                      <div className="mt-1 p-3 bg-muted rounded-md border-l-2 border-primary/40">
                        <p className="text-xs text-muted-foreground mb-1">Specificare tratament:</p>
                        <p className="text-sm whitespace-pre-wrap">{im.tratament_detalii}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Efort Fizic Tab */}
              <TabsContent value="efort" className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="w-4 h-4 text-primary" />
                      Toleranță la Efort Fizic
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <MedicalYesNoItem label="Schimbări la efort" value={ef.schimbari_efort} />
                    <InfoItem label="Toleranță efort" value={ef.toleranta_efort} />
                    <YesNoItem label="Folosește suplimente" value={ef.suplimente_nutritive} />
                    <MedicalYesNoItem label="Obosește mai repede" value={ef.oboseste_repede} />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Reviews Tab */}
              <TabsContent value="reviews" className="space-y-4">
                {/* === Alert "Consultație în curs" — re-înregistrare după ultima viză sau în general dacă atelierele nu sunt completate === */}
                {(() => {
                  const fiseList = patient.fise_complete || [];
                  const reviewsList = patient.reviews || [];
                  const fiseLen = fiseList.length;
                  // Cea mai recentă viză (dacă există)
                  const lastFisaDate = fiseLen > 0
                    ? Math.max(...fiseList.map((f) => new Date(f.date).getTime()))
                    : 0;
                  // Cel mai recent review (re-submit din formular)
                  const lastReviewDate = reviewsList.length > 0
                    ? Math.max(...reviewsList.map((r) => new Date(r.date || r.consultation_date || r.created_at || 0).getTime()))
                    : 0;
                  // Există consultație în curs dacă:
                  //   - Pacient nou fără fișe (prima viză de făcut), SAU
                  //   - Există un review mai recent decât ultima fișă (re-înregistrare)
                  const isReturning = fiseLen > 0 && lastReviewDate > lastFisaDate;
                  const isFirstVisit = fiseLen === 0;
                  if (!isReturning && !isFirstVisit) return null;
                  
                  const nextVizaNo = fiseLen + 1;
                  const ateliereDeCompletat = [1, 2, 3, 4].filter(
                    (n) => !patient[`atelier_${n}`]?.completat
                  );
                  return (
                    <Card className="border-amber-300 bg-amber-50/60" data-testid="consultatie-in-curs">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5 text-amber-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-amber-900">
                              {isReturning
                                ? `Consultație în curs — necesită viza #${nextVizaNo}`
                                : "Prima consultație — necesită completare ateliere"}
                            </h4>
                            <p className="text-sm text-amber-800 mt-1">
                              {isReturning ? (
                                <>Sportivul a revenit pentru o nouă vizită. Atelierele au fost resetate pentru această consultație, iar <strong>fișele anterioare ({fiseLen}) sunt păstrate mai jos</strong> pentru comparare în timp.</>
                              ) : (
                                <>Atelierele trebuie completate pentru a putea genera prima viză medicală.</>
                              )}
                            </p>
                            {ateliereDeCompletat.length > 0 && (
                              <p className="text-xs text-amber-700 mt-2">
                                Ateliere de completat: <strong>{ateliereDeCompletat.join(", ")}</strong> • după completarea tuturor, apăsați &bdquo;Generează nr de ordine&rdquo;
                              </p>
                            )}
                            {ateliereDeCompletat.length === 0 && (
                              <p className="text-xs text-emerald-700 mt-2 font-medium">
                                ✓ Toate atelierele sunt completate — apăsați &bdquo;Generează nr de ordine&rdquo; pentru a finaliza viza #{nextVizaNo}.
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* === Fișe medicale complete (snapshots la fiecare generare de viză) === */}
                {(patient.fise_complete || []).length > 0 && (
                  <Card data-testid="fise-complete-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Award className="w-4 h-4 text-emerald-600" />
                        Fișe medicale arhivate ({(patient.fise_complete || []).length})
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        Fiecare fișă reprezintă starea completă a sportivului (toate atelierele) la momentul generării unei vize medicale.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {[...(patient.fise_complete || [])]
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((fisa, index) => {
                          const fdate = new Date(fisa.date);
                          const luni = [
                            "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
                            "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie",
                          ];
                          const dateRo = !isNaN(fdate.getTime())
                            ? `${fdate.getDate()} ${luni[fdate.getMonth()]} ${fdate.getFullYear()}`
                            : fisa.date;
                          const concluzie = fisa.referat?.concluzie;
                          return (
                            <div
                              key={fisa.id}
                              data-testid={`fisa-row-${fisa.id}`}
                              className={`flex items-center justify-between p-3 rounded-lg border border-emerald-100 bg-emerald-50/50 animate-fade-in stagger-${Math.min(index + 1, 5)}`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                                  <Award className="w-5 h-5 text-white" />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-foreground">{dateRo}</span>
                                    <Badge className="bg-emerald-600 text-white">
                                      Nr. ord. {fisa.numar_viza}
                                    </Badge>
                                    {concluzie && (
                                      <Badge variant="outline" className="text-xs">
                                        {concluzie.replace(/_/g, " ")}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Toate atelierele completate la această dată
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => setViewingFisa(fisa)}
                                  data-testid={`view-fisa-${fisa.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                  Fișa completă
                                </Button>
                                {(() => {
                                  // Găsim ultimul snapshot Referat/Concluzii legat de aceeași viză
                                  const refSnaps = (patient.referat_snapshots || [])
                                    .filter((s) => s.numar_viza === fisa.numar_viza)
                                    .sort((a, b) => new Date(b.date) - new Date(a.date));
                                  const concSnaps = (patient.concluzii_snapshots || [])
                                    .filter((s) => s.numar_viza === fisa.numar_viza)
                                    .sort((a, b) => new Date(b.date) - new Date(a.date));
                                  const latestRef = refSnaps[0];
                                  const latestConc = concSnaps[0];
                                  return (
                                    <>
                                      {latestRef && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="gap-1"
                                          onClick={() => setViewingSnapshot({ title: `Referat medical · Viza #${fisa.numar_viza}`, html: latestRef.html })}
                                          data-testid={`view-referat-snap-${fisa.id}`}
                                        >
                                          <FileText className="w-4 h-4" /> Referat
                                        </Button>
                                      )}
                                      {latestConc && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="gap-1"
                                          onClick={() => setViewingSnapshot({ title: `Concluzii aviz · Viza #${fisa.numar_viza}`, html: latestConc.html })}
                                          data-testid={`view-concluzii-snap-${fisa.id}`}
                                        >
                                          <FileText className="w-4 h-4" /> Concluzii aviz
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                      data-testid={`delete-fisa-${fisa.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Șterge fișa?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Această fișă arhivată (viza #{fisa.numar_viza} din {dateRo}) va fi ștearsă permanent.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Anulează</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDeleteFisa(fisa.id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Șterge
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          );
                        })}
                    </CardContent>
                  </Card>
                )}

                {/* Add Review Button/Form */}
                {showReviewForm ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                        Consultație Nouă
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ReviewForm
                        onSubmit={handleAddReview}
                        isLoading={isAddingReview}
                        onCancel={() => setShowReviewForm(false)}
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <Button
                    onClick={() => setShowReviewForm(true)}
                    className="gap-2"
                    data-testid="add-review-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Adaugă Consultație
                  </Button>
                )}

                {/* Reviews List */}
                {reviews.length === 0 ? (
                  <div className="empty-state py-8">
                    <Clock className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-lg font-medium">Nu există consultații</p>
                    <p className="text-sm">Adaugă o consultație pentru a urmări istoricul</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((review, index) => (
                        <Card
                          key={review.id}
                          className={`animate-fade-in stagger-${Math.min(index + 1, 5)}`}
                          data-testid={`review-${review.id}`}
                        >
                          <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                {formatDateTime(review.date)}
                              </span>
                              {review.apt_pierdut && (
                                <Badge
                                  variant={review.apt_pierdut === "APT" ? "default" : "destructive"}
                                >
                                  {review.apt_pierdut}
                                </Badge>
                              )}
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                  data-testid={`delete-review-${review.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Șterge Consultația?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Această acțiune va șterge permanent consultația.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Anulează</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteReview(review.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Șterge
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </CardHeader>
                          <CardContent className="space-y-3 text-sm">
                            {/* Buton „Vezi formular" — deschide snapshot-ul completării formularului din această re-înscriere */}
                            {review.form_snapshot && Object.keys(review.form_snapshot).length > 0 && (
                              <div className="flex justify-start">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-2"
                                  onClick={() => setViewingSnapshot({
                                    title: `Formular completat #${review.numar_completare || "—"} · ${formatDateTime(review.date)}`,
                                    html: renderFormSnapshotHTML(review.form_snapshot),
                                  })}
                                  data-testid={`view-form-snapshot-${review.id}`}
                                >
                                  <FileText className="w-4 h-4" />
                                  Vezi formular completat
                                </Button>
                              </div>
                            )}
                            {review.observatii && (
                              <div>
                                <p className="font-medium text-muted-foreground">Observații</p>
                                <p>{review.observatii}</p>
                              </div>
                            )}
                            {review.diagnostic && (
                              <div>
                                <p className="font-medium text-muted-foreground">Diagnostic</p>
                                <p>{review.diagnostic}</p>
                              </div>
                            )}
                            {review.recomandari && (
                              <div>
                                <p className="font-medium text-muted-foreground">Recomandări</p>
                                <p>{review.recomandari}</p>
                              </div>
                            )}
                            {review.note && (
                              <div>
                                <p className="font-medium text-muted-foreground">Note</p>
                                <p>{review.note}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
              </TabsContent>

              {/* Istoric sportiv Tab */}
              <TabsContent value="istoric" className="space-y-4">
                <IstoricSportiv
                  patientId={patient.id}
                  istoric={patient.istoric_ateliere || []}
                  onChange={(newIstoric) =>
                    setPatient((prev) => ({ ...prev, istoric_ateliere: newIstoric }))
                  }
                />
              </TabsContent>

              {/* Fișiere / Atașamente Tab */}
              <TabsContent value="fisiere" className="space-y-4">
                <AttachmentsSection
                  title="Fișiere atașate de pacient"
                  description="Fișierele încărcate de sportiv direct din formularul public (analize, poze, PDF-uri). Doar administratorul le poate șterge."
                  attachments={(patient.attachments || []).filter((a) => a.source === "patient")}
                  onDelete={handleAttachmentDelete}
                  onView={handleAttachmentView}
                  onAttachmentRemoved={(fid) =>
                    setPatient((prev) => ({
                      ...prev,
                      attachments: (prev.attachments || []).filter((a) => a.id !== fid),
                    }))
                  }
                  canUpload={false}
                  canDelete={true}
                  emptyText="Pacientul nu a atașat niciun fișier."
                  testIdPrefix="patient-files-patient"
                />

                <AttachmentsSection
                  title="Fișiere atașate de admin"
                  description="Documente încărcate de administrator (rezultate analize, referate, poze suplimentare). Acceptate: JPG, PNG, HEIC, PDF — max 10 fișiere × 10 MB."
                  attachments={(patient.attachments || []).filter((a) => a.source === "admin")}
                  onUpload={handleAdminUpload}
                  onDelete={handleAttachmentDelete}
                  onView={handleAttachmentView}
                  onAttachmentAdded={() => { /* handleAdminUpload deja actualizează state-ul */ }}
                  onAttachmentRemoved={(fid) =>
                    setPatient((prev) => ({
                      ...prev,
                      attachments: (prev.attachments || []).filter((a) => a.id !== fid),
                    }))
                  }
                  canUpload={true}
                  canDelete={true}
                  emptyText="Nu ai atașat încă niciun fișier ca administrator."
                  testIdPrefix="patient-files-admin"
                />

                {/* Acte medicale necesare de adus — text liber opțional, salvare la blur */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      Acte medicale necesare de adus
                      {savingActe && (
                        <span className="text-xs font-normal text-muted-foreground">Se salvează…</span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Label htmlFor="acte-medicale" className="text-xs text-muted-foreground">
                      Câmp opțional — listează actele medicale pe care sportivul trebuie să le aducă la următoarea vizită. Se salvează automat la click în afara câmpului.
                    </Label>
                    <Textarea
                      id="acte-medicale"
                      value={acteMedicale}
                      onChange={(e) => setActeMedicale(e.target.value)}
                      onBlur={handleActeMedicaleBlur}
                      placeholder="Ex: analize sânge complete, EKG cu efort, aviz cardiolog, radiografie genunchi..."
                      className="mt-2 min-h-[100px] text-sm"
                      data-testid="acte-medicale-textarea"
                    />
                  </CardContent>
                </Card>

                {/* Observații atașamente — se salvează automat la ieșirea din câmp */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      Observații atașamente
                      {savingNotes && (
                        <span className="text-xs font-normal text-muted-foreground">Se salvează…</span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Label htmlFor="admin-notes" className="text-xs text-muted-foreground">
                      Text liber (observații, task-uri, memento-uri) — vizibil doar administratorilor. Se salvează automat la click în afara câmpului.
                    </Label>
                    <Textarea
                      id="admin-notes"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      onBlur={handleAdminNotesBlur}
                      placeholder="Ex: rezultat analize verificat cu medicul de familie / a adus adeverința de la club / trebuie sunat pentru control repetat..."
                      className="mt-2 min-h-[180px] text-sm"
                      data-testid="admin-notes-textarea"
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground text-right tabular-nums" data-testid="admin-notes-counter">
                      {adminNotes.length} caractere
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      {/* Dialog vizualizare fișă completă */}
      <FisaCompletaDialog
        fisa={viewingFisa}
        open={!!viewingFisa}
        onClose={() => setViewingFisa(null)}
      />

      {/* Dialog snapshot HTML — Referat / Concluzii aviz */}
      {viewingSnapshot && (
        <Dialog open={true} onOpenChange={(v) => !v && setViewingSnapshot(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{viewingSnapshot.title}</DialogTitle>
            </DialogHeader>
            <div className="border rounded p-4 bg-white" data-testid="snapshot-viewer" dangerouslySetInnerHTML={{ __html: viewingSnapshot.html }} />
            <div className="flex justify-end gap-2 mt-2">
              <Button
                variant="outline"
                onClick={() => {
                  const w = window.open("", "_blank", "width=900,height=700");
                  if (!w) return;
                  const styles = Array.from(document.querySelectorAll("style,link[rel=stylesheet]"))
                    .map((s) => s.outerHTML).join("\n");
                  w.document.write(`<!DOCTYPE html><html lang="ro"><head><meta charset="utf-8"/><title>${viewingSnapshot.title}</title>${styles}<style>@page{size:A4;margin:14mm}body{background:#fff;color:#111;font-family:'Times New Roman',Georgia,serif;padding:12px;margin:0}</style></head><body>${viewingSnapshot.html}<script>setTimeout(()=>window.print(),200)</script></body></html>`);
                  w.document.close();
                }}
                data-testid="snapshot-print-btn"
              >
                Printează
              </Button>
              <Button onClick={() => setViewingSnapshot(null)}>Închide</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
